import React, { useCallback, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import {
  noop,
  ScrollViewWithHeaderTitle,
  theme,
  useToggleState,
} from '@sweetgreen/garnish';

import { CreditCardAddModal, CreditCardEditModal } from '@order/components';
import type { PaymentMethod } from '@order/graphql';
import { usePaymentMethods } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import { AccountScreenTitle } from '../../../components/Account';
import { PaymentMethodList } from '../../../components/PaymentMethod';
import { TransparentModal } from '../../../components/TransparentModal';

export const ScanToPayPayments = () => {
  const { t } = useLocalizationContext();
  const title = t('account.scan-to-pay.payments');

  const { bottom, top } = useSafeAreaInsets();
  const dynamicPadding = useStyle(() => {
    return {
      paddingTop: Math.max(theme.spacing['4'], top),
      paddingBottom: Math.max(theme.spacing['4'], bottom),
    };
  }, [top, bottom]);

  // ─── Payment Methods ─────────────────────────────────────────────────

  const { paymentMethods } = usePaymentMethods();

  // ─── State ───────────────────────────────────────────────────────────

  const [selectedPaymentMethodId, selectPaymentMethodId] = useState('');

  // ─── Toggles ─────────────────────────────────────────────────────────

  const {
    value: isShowingAddPaymentMethodModal,
    toggleOn: showAddPaymentMethodModal,
    toggleOff: hideAddPaymentMethodModal,
  } = useToggleState();

  const showEditPaymentMethodModal = useCallback(
    (paymentMethod: PaymentMethod) => {
      selectPaymentMethodId(paymentMethod.id);
    },
    [],
  );

  const hideEditPaymentMethodModal = useCallback(() => {
    selectPaymentMethodId('');
  }, []);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal size="full" onClose={noop}>
      <ScrollViewWithHeaderTitle testID="stp.payments.root.container">
        <View
          testID="scan-to-pay.payments.route"
          style={[styles.padding, dynamicPadding]}
        >
          <AccountScreenTitle title={title} />
          <PaymentMethodList
            items={paymentMethods}
            onSelectItem={showEditPaymentMethodModal}
            onSelectAdd={showAddPaymentMethodModal}
          />
        </View>
      </ScrollViewWithHeaderTitle>

      <CreditCardAddModal
        visible={isShowingAddPaymentMethodModal}
        onRequestClose={hideAddPaymentMethodModal}
      />

      <CreditCardEditModal
        paymentMethodId={selectedPaymentMethodId}
        visible={Boolean(selectedPaymentMethodId)}
        onRequestClose={hideEditPaymentMethodModal}
      />
    </TransparentModal>
  );
};

const styles = StyleSheet.create({
  padding: {
    flex: 1,
    backgroundColor: theme.colors.OATMEAL,
    paddingHorizontal: theme.spacing['4'],
  },
});
